import type { CSSObject } from 'styled-components';
import styled from 'styled-components';

import { CloseIcon } from '@genially/icons';

import { BasicParagraph } from '../../../foundations/typefaces/BasicParagraph';
import { SmallSubtitle } from '../../../foundations/typefaces/SmallSubtitle';
import style from '../../../style';
import type { Color } from '../../../style/types/theme';
import { isSafari } from '../../../utils/devices/device';
import { Variant } from './types';

interface VariantProps {
  $variant: Variant;
}

interface WrapperProps {
  $show?: boolean;
}

type ContainerProps = VariantProps & WrapperProps;

const getVariantColor = (
  $variant: Variant,
  color: Color,
): CSSObject & { hoverColor: string } => {
  switch ($variant) {
    case Variant.WARNING:
      return {
        backgroundColor: color.background.warning.default(),
        color: color.content.primary.default(),
        hoverColor: color.content.warning.hoverContrast(),
      };
    case Variant.DANGER:
      return {
        backgroundColor: color.background.error.default(),
        color: color.content.primary.default(),
        hoverColor: color.content.error.hoverContrast(),
      };
    case Variant.INFO:
    default:
      return {
        backgroundColor: color.background.info.default(),
        color: color.content.primary.default(),
        hoverColor: color.content.info.hoverContrast(),
      };
  }
};

const getVariantIconColor = ($variant: Variant, color: Color): CSSObject => {
  switch ($variant) {
    case Variant.WARNING:
      return {
        color: color.content.warning.default(),
      };
    case Variant.DANGER:
      return {
        color: color.content.error.default(),
      };
    case Variant.INFO:
    default:
      return {
        color: color.content.info.default(),
      };
  }
};

export const Container = styled.div<ContainerProps>(
  ({ theme: { size, color, transition }, $show = false, $variant }) => ({
    display: 'flex',
    width: '100%',
    alignItems: 'baseline',
    opacity: $show ? 1 : 0,
    backgroundColor: getVariantColor($variant, color).backgroundColor,
    color: getVariantColor($variant, color).color,
    padding: `${size.units(1.5)} ${size.units(2)}`,
    transition: transition.standard('top', 'opacity'),
    userSelect: $show ? undefined : 'none',
    pointerEvents: $show ? undefined : 'none',

    ...style.mixins.responsive.ipad.downward({
      alignItems: 'flex-start',
    }),
  }),
);

export const Wrapper = styled.div<WrapperProps>({
  width: '100%',
});

export const StyledBasicParagraph = styled(BasicParagraph)<VariantProps>(
  ({ theme: { color }, $variant }) => ({
    color: getVariantColor($variant, color).color,
    flexWrap: 'wrap',
  }),
);

export const StyledButtonText = styled(SmallSubtitle)<VariantProps>(
  ({ theme: { color }, $variant }) => ({
    display: 'flex',
    color: getVariantColor($variant, color).color,

    ':hover': {
      color: getVariantColor($variant, color).hoverColor,
      cursor: 'pointer',
    },

    ...style.mixins.responsive.ipad.downward({
      flexBasis: '100%',
      alignSelf: 'start',
    }),
  }),
);

export const ButtonWrapper = styled.div({
  minWidth: 'fit-content',
});

export const ContentWrapper = styled.div<WrapperProps>(
  ({ theme: { size }, $show = false }) => ({
    width: '100%',
    display: isSafari ? '-webkit-flex' : 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    opacity: $show ? 1 : 0,
    transform: $show ? `translateY(0)` : `translateY(${size.units(-1.5)})`,
    transition:
      'transform .2s cubic-bezier(0.4, -0.7, 0.4, 1.7) 0.05s, opacity .2s cubic-bezier(0.4, -0.7, 0.4, 1.7) 0.05s',

    ...style.mixins.responsive.ipad.downward({
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
    }),

    [StyledBasicParagraph]: {
      marginLeft: size.units(1),
    },

    [ButtonWrapper]: {
      ...style.mixins.responsive.ipad.upward({
        marginLeft: `${size.units(1)}`,
      }),

      ...style.mixins.responsive.ipad.downward({
        marginTop: size.units(1),
        marginLeft: size.units(2.5),
      }),
    },
  }),
);

export const StyledCloseIcon = styled(CloseIcon)({
  '&:hover': {
    cursor: 'pointer',
  },
});

export const IconWrapper = styled.div<WrapperProps>(
  ({ theme: { size }, $show = false }) => ({
    display: 'flex',
    fontSize: size.units(2),
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: $show ? 'auto' : 'none',

    ...style.mixins.responsive.ipad.downward({
      display: 'block',
      alignSelf: 'stretch',
    }),

    [StyledCloseIcon]: {
      marginLeft: size.units(1),
    },
  }),
);

export const StyledVariantIcon = styled.i<WrapperProps & VariantProps>(
  ({ theme: { color, size }, $show = false, $variant }) => ({
    pointerEvents: $show ? 'auto' : 'none',
    flexShrink: 0,
    width: size.units(2),
    height: size.units(2),
    color: getVariantIconColor($variant, color).color,
  }),
);

export const IconTextWrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',

  [StyledVariantIcon]: {
    marginTop: size.units(0.25),
    marginLeft: size.units(3),
  },

  [IconWrapper]: {
    marginLeft: size.units(3),
  },

  ...style.mixins.responsive.ipad.downward({
    [`${IconWrapper}, ${StyledVariantIcon}`]: {
      marginLeft: 0,
    },
  }),
}));
